import { ToWords } from 'to-words';
import { type ISolution } from '../reducers/solutionsSlice';

/**
 * Validates customer name format
 * @param title - The customer name to validate
 * @returns true if valid, false otherwise
 */
export function isCustomerNameValid(title: string): boolean {
  const regex = /^[a-zA-Z0-9]+[a-zA-Z0-9_-\s\.]*$/;
  return regex.test(title);
}

/**
 * Validates solution prefix format
 * @param prefix - The solution prefix to validate
 * @returns true if valid, false otherwise
 */
export function isSolutionPrefixValid(prefix: string): boolean {
  const regex = /^(?!.*[_-]{2})[a-zA-Z0-9]+[a-zA-Z0-9_-]*$/;
  return regex.test(prefix);
}

/**
 * Generates a solution title/prefix from customer name and template title
 * @param customerName - The customer name
 * @param templateTitle - The template/solution title
 * @param customerNameSuffix - Optional suffix from listing.yml
 * @returns The generated solution prefix
 */
export function getSolutionTitle(customerName: string, templateTitle: string, customerNameSuffix?: string): string {
  let filteredTitle = `${customerName}_${templateTitle ?? ''}`;

  // If customer_name_suffix exists in listing.yml use that to generate solution prefix
  if (customerNameSuffix !== undefined && customerNameSuffix !== '') {
    filteredTitle = `${customerName}_${customerNameSuffix}`;
  }

  filteredTitle = filteredTitle.replaceAll(/[^a-zA-Z0-9_\-]/g, '').replaceAll('-', '');

  // If the first character is a number, replace it with its word equivalent.
  if (filteredTitle.charAt(0).match(/[0-9]/) !== null) {
    filteredTitle = filteredTitle.replace(
      filteredTitle.charAt(0),
      new ToWords().convert(parseInt(filteredTitle.charAt(0))),
    );
  }

  filteredTitle = filteredTitle.toUpperCase();
  return filteredTitle;
}

/**
 * Gets the correct solution page path based on the solution's listing kind
 * @param solutionId - The solution ID
 * @param solution - The solution object (optional)
 * @returns The path to the solution page (/solutions/:id or /event-solutions/:id)
 */
export function getSolutionPath(solutionId: string, solution?: ISolution): string {
  const isEventSolution = solution?.listing?.listing_kind === 'event_solution';
  return isEventSolution ? `/event-solutions/${solutionId}` : `/solutions/${solutionId}`;
}
